/**
 * Page Card Component
 *
 * Dedicated component for 'page' and 'article' resource items (general web content,
 * stories, blog posts, guides). Both types share the page schema: name, description,
 * content, photos and categories. These items usually have no coordinates, so they are
 * shown in the blocks feed rather than on the map.
 * Supports both feed card and map popup rendering via the `variant` prop.
 */

import { h } from 'preact';
import htm from 'htm';
import { CardMedia } from './shared/CardMedia.js';
import { CardDescription } from './shared/CardDescription.js';
import { CardLinks } from './shared/CardLinks.js';
import { pickText, label } from './shared/fields.js';

const html = htm.bind(h);

/**
 * Renders a page or article item as a result card or map popup.
 *
 * @param {Object} props
 * @param {Object} props.item - The page/article item data.
 * @param {Object} [props.block] - Parent block data.
 * @param {Object} props.ctx - View context with `t` and `config`.
 * @param {string} [props.variant='card'] - 'card' for blocks feed or 'popup' for map infowindow.
 * @param {Function} [props.onClose] - Callback when the popup close button is clicked.
 */
export function PageCard({ item, block, ctx, variant = 'card', onClose }) {
  const isPopup = variant === 'popup';
  const isArticle = item?.type === 'article';

  const tag = pickText(item, 'categories', 'category', 'tag') || (isArticle ? label(ctx, 'type_article', 'Article') : '');
  const name = pickText(item, 'name', 'label', 'title');
  // `description` is a short summary; fall back to the full text when it is missing.
  const description = pickText(item, 'description', 'content');
  const website = pickText(item, 'website');
  const sourceUrl = pickText(item, 'source_url', 'url');

  const containerClass = isPopup
    ? 'mta-result-card mta-card--popup mta-map-popup-card mta-map-popup-theme'
    : 'mta-result-card';

  return html`
    <div class="${containerClass}">
      <${CardMedia} item=${item} block=${block} ctx=${ctx} variant=${variant} onClose=${onClose} />

      ${tag ? html`<div class="mta-card-tag">${tag}</div>` : ''}
      <h3 class="mta-card-title">${name}</h3>

      <${CardDescription} description=${description} ctx=${ctx} variant=${variant} />

      <${CardLinks} links=${[
        { href: sourceUrl, text: isArticle ? label(ctx, 'read_article', 'Read article') : label(ctx, 'show_on_site', 'Show on site') },
        { href: website, text: label(ctx, 'website', 'Website') }
      ]} />
    </div>
  `;
}
